import { useCallback, useEffect, useRef, useState } from 'react'
import kitApi from '@/api/kit/KitApi'
import type {
    KitConvoCompanyMentionDto,
    KitConvoDebugConversationDto,
    KitConvoDebugScoringDto,
    KitConvoCurrentJobSearchParamsDto,
    KitConvoJobOfferedDto,
    KitConvoJobSearchExecutionDto,
    KitConvoLocationMentionDto,
    KitConvoPersonMentionDto,
    KitConvoTranscriptRawDataDto,
    KitConvoTranscriptTurnDto,
    KitConvoTargetSalaryMentionDto,
    KitConvoTargetJobTitleMentionDto,
    KitConvoToolCallDto,
} from '@/api/kit/KitApi'

type Props = {
    conversationId: string | null
    isActiveCall: boolean
    pollIntervalMs?: number
    flashDurationMs?: number
}

type Result = {
    conversation: KitConvoDebugConversationDto | null
    toolCalls: KitConvoToolCallDto[]
    transcriptRawData: KitConvoTranscriptRawDataDto | null
    transcriptTurns: KitConvoTranscriptTurnDto[]
    personMentions: KitConvoPersonMentionDto[]
    companyMentions: KitConvoCompanyMentionDto[]
    locationMentions: KitConvoLocationMentionDto[]
    targetJobTitleMentions: KitConvoTargetJobTitleMentionDto[]
    targetSalaryMentions: KitConvoTargetSalaryMentionDto[]
    currentJobSearchParams: KitConvoCurrentJobSearchParamsDto | null
    jobsOffered: KitConvoJobOfferedDto[]
    jobSearchExecutions: KitConvoJobSearchExecutionDto[]
    scoring: KitConvoDebugScoringDto | null
    toolCallsError: string | null
    flashingToolCallIds: Record<string, boolean>
    hasFetchedOnce: boolean
    resetDebugState: () => void
    refreshDebugData: () => Promise<boolean>
}

const DEFAULT_POLL_INTERVAL_MS = 500
const DEFAULT_FLASH_DURATION_MS = 1500

export default function useKitConvoDebug({
    conversationId,
    isActiveCall,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    flashDurationMs = DEFAULT_FLASH_DURATION_MS,
}: Props): Result {
    const [conversation, setConversation] =
        useState<KitConvoDebugConversationDto | null>(null)
    const [toolCalls, setToolCalls] = useState<KitConvoToolCallDto[]>([])
    const [transcriptRawData, setTranscriptRawData] =
        useState<KitConvoTranscriptRawDataDto | null>(null)
    const [transcriptTurns, setTranscriptTurns] = useState<
        KitConvoTranscriptTurnDto[]
    >([])
    const [personMentions, setPersonMentions] = useState<
        KitConvoPersonMentionDto[]
    >([])
    const [companyMentions, setCompanyMentions] = useState<
        KitConvoCompanyMentionDto[]
    >([])
    const [locationMentions, setLocationMentions] = useState<
        KitConvoLocationMentionDto[]
    >([])
    const [targetJobTitleMentions, setTargetJobTitleMentions] = useState<
        KitConvoTargetJobTitleMentionDto[]
    >([])
    const [targetSalaryMentions, setTargetSalaryMentions] = useState<
        KitConvoTargetSalaryMentionDto[]
    >([])
    const [currentJobSearchParams, setCurrentJobSearchParams] =
        useState<KitConvoCurrentJobSearchParamsDto | null>(null)
    const [jobsOffered, setJobsOffered] = useState<KitConvoJobOfferedDto[]>([])
    const [jobSearchExecutions, setJobSearchExecutions] = useState<
        KitConvoJobSearchExecutionDto[]
    >([])
    const [scoring, setScoring] = useState<KitConvoDebugScoringDto | null>(null)
    const [toolCallsError, setToolCallsError] = useState<string | null>(null)
    const [flashingToolCallIds, setFlashingToolCallIds] = useState<
        Record<string, boolean>
    >({})
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
    const toolCallPollTimeoutRef = useRef<number | null>(null)
    const toolCallFlashTimeoutsRef = useRef<Record<string, number>>({})
    const fetchPromiseRef = useRef<Promise<boolean> | null>(null)
    const activeConversationIdRef = useRef<string | null>(conversationId)

    useEffect(() => {
        activeConversationIdRef.current = conversationId
        fetchPromiseRef.current = null
    }, [conversationId])

    const clearToolCallFlashTimeouts = useCallback(() => {
        Object.values(toolCallFlashTimeoutsRef.current).forEach((timeoutId) => {
            window.clearTimeout(timeoutId)
        })
        toolCallFlashTimeoutsRef.current = {}
    }, [])

    const triggerToolCallFlash = useCallback(
        (ids: string[]) => {
            if (!ids.length) return

            setFlashingToolCallIds((prev) => {
                const next = { ...prev }
                ids.forEach((id) => {
                    next[id] = true
                })
                return next
            })

            ids.forEach((id) => {
                const existingTimeout = toolCallFlashTimeoutsRef.current[id]
                if (existingTimeout) {
                    window.clearTimeout(existingTimeout)
                }

                toolCallFlashTimeoutsRef.current[id] = window.setTimeout(() => {
                    setFlashingToolCallIds((prev) => {
                        const next = { ...prev }
                        delete next[id]
                        return next
                    })
                    delete toolCallFlashTimeoutsRef.current[id]
                }, flashDurationMs)
            })
        },
        [flashDurationMs],
    )

    const resetDebugState = useCallback(() => {
        setConversation(null)
        setToolCalls([])
        setTranscriptRawData(null)
        setTranscriptTurns([])
        setPersonMentions([])
        setCompanyMentions([])
        setLocationMentions([])
        setTargetJobTitleMentions([])
        setTargetSalaryMentions([])
        setCurrentJobSearchParams(null)
        setJobsOffered([])
        setJobSearchExecutions([])
        setScoring(null)
        setToolCallsError(null)
        setFlashingToolCallIds({})
        setHasFetchedOnce(false)
        clearToolCallFlashTimeouts()
    }, [clearToolCallFlashTimeouts])

    const fetchDebugData = useCallback(async (): Promise<boolean> => {
        const normalizedConvId = conversationId?.trim()

        if (!normalizedConvId) {
            setHasFetchedOnce(false)
            return false
        }

        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current
        }

        const promise = (async () => {
            try {
                const res = await kitApi.getConvoDebug(normalizedConvId)

                if (activeConversationIdRef.current !== normalizedConvId) {
                    return false
                }

                setToolCalls((prev) => {
                    const prevIds = new Set(prev.map((call) => call.id))
                    const newIds = res.toolCalls
                        .filter((call) => !prevIds.has(call.id))
                        .map((call) => call.id)
                    if (newIds.length > 0) {
                        triggerToolCallFlash(newIds)
                    }
                    return res.toolCalls
                })
                setConversation(res.conversation ?? null)
                setTranscriptRawData(res.transcriptRawData ?? null)
                setTranscriptTurns(res.transcriptTurns ?? [])
                setPersonMentions(res.personMentions)
                setCompanyMentions(res.usedToWorkAtCompanyMentions ?? [])
                setLocationMentions(res.targetJobLocationMentions ?? [])
                setTargetJobTitleMentions(res.targetJobTitleMentions ?? [])
                setTargetSalaryMentions(res.targetSalaryMentions ?? [])
                setCurrentJobSearchParams(res.currentJobSearchParams ?? null)
                setJobsOffered(res.jobsOffered ?? [])
                setJobSearchExecutions(res.jobSearchExecutions ?? [])
                setScoring(res.scoring ?? null)
                setToolCallsError(null)
                return true
            } catch (err) {
                if (activeConversationIdRef.current === normalizedConvId) {
                    console.error('Failed to load kit convo debug data', err)
                    setToolCallsError(
                        err instanceof Error
                            ? err.message
                            : 'Failed to load debug data.',
                    )
                }
                return false
            } finally {
                if (activeConversationIdRef.current === normalizedConvId) {
                    setHasFetchedOnce(true)
                }
                fetchPromiseRef.current = null
            }
        })()

        fetchPromiseRef.current = promise
        return promise
    }, [conversationId, triggerToolCallFlash])

    useEffect(() => {
        return () => {
            if (toolCallPollTimeoutRef.current !== null) {
                window.clearTimeout(toolCallPollTimeoutRef.current)
            }
            clearToolCallFlashTimeouts()
            fetchPromiseRef.current = null
        }
    }, [clearToolCallFlashTimeouts])

    useEffect(() => {
        resetDebugState()

        if (!conversationId) {
            return
        }

        void fetchDebugData()
    }, [conversationId, fetchDebugData, resetDebugState])

    useEffect(() => {
        if (!conversationId || !isActiveCall) {
            if (toolCallPollTimeoutRef.current !== null) {
                window.clearTimeout(toolCallPollTimeoutRef.current)
                toolCallPollTimeoutRef.current = null
            }
            return
        }

        let cancelled = false

        const poll = async () => {
            if (cancelled) {
                return
            }

            await fetchDebugData()

            if (!cancelled) {
                toolCallPollTimeoutRef.current = window.setTimeout(
                    poll,
                    pollIntervalMs,
                )
            }
        }

        poll()

        return () => {
            cancelled = true
            if (toolCallPollTimeoutRef.current !== null) {
                window.clearTimeout(toolCallPollTimeoutRef.current)
                toolCallPollTimeoutRef.current = null
            }
        }
    }, [conversationId, fetchDebugData, isActiveCall, pollIntervalMs])

    const refreshDebugData = useCallback(async () => {
        return await fetchDebugData()
    }, [fetchDebugData])

    return {
        conversation,
        toolCalls,
        transcriptRawData,
        transcriptTurns,
        personMentions,
        companyMentions,
        locationMentions,
        targetJobTitleMentions,
        targetSalaryMentions,
        currentJobSearchParams,
        jobsOffered,
        jobSearchExecutions,
        scoring,
        toolCallsError,
        flashingToolCallIds,
        hasFetchedOnce,
        resetDebugState,
        refreshDebugData,
    }
}
